import { Clock, Loader2, RotateCcw, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/AlertDialog.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Card, CardContent } from "../../components/ui/Card.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { useToast } from "../../hooks/useToast.js";
import { fetchTrash, restoreCompany, permanentDeleteCompany } from "../../services/superAdminService.js";

export default function Trash() {
  const { toast } = useToast();
  const [data, setData] = useState({ companies: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Restore confirm
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [restoring, setRestoring] = useState(false);

  // Permanent delete confirm
  const [permDeleteConfirm, setPermDeleteConfirm] = useState(null);
  const [permDeleting, setPermDeleting] = useState(false);

  const loadTrash = async (p = page) => {
    setLoading(true);
    try {
      const res = await fetchTrash({ page: p, limit: 20, search });
      setData(res.data || { companies: [], users: [] });
      setPagination(res.pagination || {});
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrash(1);
    setPage(1);
  }, [search]);

  const handleRestore = async () => {
    if (!restoreConfirm) return;
    setRestoring(true);
    try {
      await restoreCompany(restoreConfirm.id);
      toast({ title: `"${restoreConfirm.name}" restored successfully with all data` });
      setRestoreConfirm(null);
      await loadTrash(page);
    } catch (err) {
      toast({ variant: "destructive", title: "Restore failed", description: err.message });
    } finally {
      setRestoring(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!permDeleteConfirm) return;
    setPermDeleting(true);
    try {
      await permanentDeleteCompany(permDeleteConfirm.id);
      toast({ title: `"${permDeleteConfirm.name}" permanently deleted` });
      setPermDeleteConfirm(null);
      await loadTrash(page);
    } catch (err) {
      toast({ variant: "destructive", title: "Delete failed", description: err.message });
    } finally {
      setPermDeleting(false);
    }
  };

  const getRetentionBadge = (daysRemaining) => {
    if (daysRemaining <= 7)
      return "bg-red-50 text-red-600";
    if (daysRemaining <= 20)
      return "bg-orange-50 text-orange-600";
    return "bg-gray-100 text-gray-600";
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Trash / Recycle Bin</h1>
        <p className="text-xs text-gray-500 mt-1">
          Deleted companies and users are kept for 60 days before permanent deletion. You can restore them with one click.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search deleted companies or users..."
          className="h-8 text-xs pl-9"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* Deleted Companies */}
          {data.companies.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-700">
                Deleted Companies ({data.companies.length})
              </h2>

              <Card className="border-gray-100 overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-500">Company</th>
                          <th className="text-left p-3 font-medium text-gray-500">Admin</th>
                          <th className="text-left p-3 font-medium text-gray-500">Industry</th>
                          <th className="text-left p-3 font-medium text-gray-500">Users</th>
                          <th className="text-left p-3 font-medium text-gray-500">Leads</th>
                          <th className="text-left p-3 font-medium text-gray-500">Deleted On</th>
                          <th className="text-left p-3 font-medium text-gray-500">Deleted By</th>
                          <th className="text-left p-3 font-medium text-gray-500">Retention</th>
                          <th className="text-left p-3 font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.companies.map((c) => (
                          <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="p-3">
                              <p className="font-medium text-gray-800">{c.company_name}</p>
                              <p className="text-[10px] text-gray-400">{c.company_email}</p>
                            </td>
                            <td className="p-3">
                              <p className="text-gray-700">{c.admin_name || "-"}</p>
                              <p className="text-[10px] text-gray-400">{c.admin_email}</p>
                            </td>
                            <td className="p-3">
                              {c.industry_type ? (
                                <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px]">
                                  {c.industry_type}
                                </span>
                              ) : "-"}
                            </td>
                            <td className="p-3 text-gray-700">{c.user_count}</td>
                            <td className="p-3 text-gray-700">{c.lead_count}</td>
                            <td className="p-3 text-gray-500">{formatDate(c.deleted_at)}</td>
                            <td className="p-3 text-gray-500">{c.deleted_by_name || "-"}</td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${getRetentionBadge(c.days_remaining)}`}>
                                <Clock size={9} />
                                {c.days_remaining > 0
                                  ? `${c.days_remaining}d left`
                                  : "Expiring soon"}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-[10px] gap-1 rounded-full border-green-200 text-green-600 hover:bg-green-50"
                                  onClick={() =>
                                    setRestoreConfirm({
                                      id: c.id,
                                      name: c.company_name,
                                      admin_name: c.admin_name,
                                      user_count: c.user_count,
                                      lead_count: c.lead_count,
                                    })
                                  }
                                >
                                  <RotateCcw size={10} />
                                  Restore
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-[10px] gap-1 rounded-full border-red-200 text-red-500 hover:bg-red-50"
                                  onClick={() =>
                                    setPermDeleteConfirm({
                                      id: c.id,
                                      name: c.company_name,
                                    })
                                  }
                                >
                                  <Trash2 size={10} />
                                  Delete Forever
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Deleted Users (standalone, not part of a company deletion) */}
          {data.users.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-700">
                Deleted Users ({data.users.length})
              </h2>

              <Card className="border-gray-100 overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-500">Name</th>
                          <th className="text-left p-3 font-medium text-gray-500">Email</th>
                          <th className="text-left p-3 font-medium text-gray-500">Role</th>
                          <th className="text-left p-3 font-medium text-gray-500">Deleted On</th>
                          <th className="text-left p-3 font-medium text-gray-500">Deleted By</th>
                          <th className="text-left p-3 font-medium text-gray-500">Retention</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.users.map((u) => (
                          <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="p-3 font-medium text-gray-800">{u.name}</td>
                            <td className="p-3 text-gray-600">{u.email}</td>
                            <td className="p-3">
                              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px]">
                                {u.user_role}
                              </span>
                            </td>
                            <td className="p-3 text-gray-500">{formatDate(u.deleted_at)}</td>
                            <td className="p-3 text-gray-500">{u.deleted_by_name || "-"}</td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${getRetentionBadge(u.days_remaining)}`}>
                                <Clock size={9} />
                                {u.days_remaining > 0
                                  ? `${u.days_remaining}d left`
                                  : "Expiring soon"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Empty state */}
          {data.companies.length === 0 && data.users.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <RotateCcw size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Trash is empty</p>
              <p className="text-xs mt-1">Deleted companies and users will appear here</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.pages}
              </p>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={page <= 1}
                  onClick={() => { setPage(page - 1); loadTrash(page - 1); }}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={page >= pagination.pages}
                  onClick={() => { setPage(page + 1); loadTrash(page + 1); }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Restore Confirm Dialog */}
      <AlertDialog open={!!restoreConfirm} onOpenChange={(open) => !open && setRestoreConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore "{restoreConfirm?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-gray-500 space-y-2">
                <p>This will restore everything in one click:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Company profile and settings</li>
                  <li>Business admin ({restoreConfirm?.admin_name || "owner"}) — unsuspended</li>
                  <li>{restoreConfirm?.user_count || 0} team member(s) — unsuspended</li>
                  <li>{restoreConfirm?.lead_count || 0} lead(s)</li>
                  <li>All custom fields and dropdown options</li>
                </ul>
                <p className="text-xs text-gray-400">All accounts will be reactivated immediately.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} disabled={restoring}>
              {restoring ? "Restoring..." : "Restore All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Dialog */}
      <AlertDialog open={!!permDeleteConfirm} onOpenChange={(open) => !open && setPermDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete "{permDeleteConfirm?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-gray-500 space-y-2">
                <p className="text-red-500 font-medium">This action cannot be undone.</p>
                <p>This will permanently remove from the database:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Company and all settings</li>
                  <li>Business admin account</li>
                  <li>All team member accounts</li>
                  <li>All leads</li>
                  <li>All custom fields and options</li>
                  <li>All lead stages</li>
                </ul>
                <p className="text-xs text-red-400">The email addresses will be freed up for re-registration.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePermanentDelete}
              disabled={permDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {permDeleting ? "Deleting..." : "Delete Forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
